import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import axios from "axios";
import { API_BASE } from "../../config";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../supaBaseClient";
import Footer from "../homepage/footer";

const CompletedTopics = () => {
  const [completedTopics, setCompletedTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userId, setUserId] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    let unsub = () => {};

    (async () => {
      const { data, error } = await supabase.auth.getUser();
      if (!error && data?.user) {
        setUserId(data.user.id);
      } else {
        navigate("/signin");
      }

      const {
        data: { subscription },
      } = supabase.auth.onAuthStateChange((_event, session) => {
        const user = session?.user;
        if (user) setUserId(user.id);
        else navigate("/signin");
      });

      unsub = () => subscription.unsubscribe();
    })();

    return () => unsub();
  }, [navigate]);

  useEffect(() => {
    if (!userId) return;

    const fetchCompletedTopics = async () => {
      try {
        const res = await axios.get(`${API_BASE}/api/topics/completed/${userId}`);
        setCompletedTopics(res.data);
      } catch (err) {
        console.error("Error fetching completed topics:", err);
        setError("Failed to fetch completed topics.");
      } finally {
        setLoading(false);
      }
    };

    fetchCompletedTopics();
  }, [userId]);

  // Group topics by category/subject if available
  const groupedTopics = completedTopics.reduce((acc, topic) => {
    const category = topic.category || 'Other';
    if (!acc[category]) acc[category] = [];
    acc[category].push(topic);
    return acc;
  }, {});

  // Calculate statistics
  const totalCompleted = completedTopics.length;
  const thisMonth = completedTopics.filter(topic => {
    const completionDate = new Date(topic.completion_date);
    const now = new Date();
    return completionDate.getMonth() === now.getMonth() && 
           completionDate.getFullYear() === now.getFullYear();
  }).length;

  const thisWeek = completedTopics.filter(topic => {
    const completionDate = new Date(topic.completion_date);
    const now = new Date();
    const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    return completionDate >= weekAgo;
  }).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-teal-600 mx-auto mb-4"></div>
          <p className="text-teal-600 font-semibold">Loading completed topics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md">
          <div className="bg-red-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <p className="text-center text-red-600 font-semibold">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      <Helmet>
        <title>Completed Topics | MLMaths</title>
        <link rel="canonical" href="https://mlmaths.co.uk/completedTopics" />
        <meta name="description" content="View all the topics you've completed on MLMaths and track your learning achievements." />
      </Helmet>

      <main className="container mx-auto py-10 px-4 sm:px-6 lg:px-12 max-w-7xl flex-grow">
        {/* Header Section */}
        <div className="text-center mb-10">
          <div className="bg-teal-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-10 h-10 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h1 className="text-4xl font-bold text-gray-800 mb-2">Completed Topics</h1>
          <p className="text-lg text-gray-600">Track your learning achievements and milestones</p>
        </div>

        {/* Statistics Cards */}
        {totalCompleted > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white rounded-2xl shadow-lg p-6 border-l-4 border-teal-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 text-sm font-medium mb-1">Total Completed</p>
                  <p className="text-3xl font-bold text-gray-800">{totalCompleted}</p>
                </div>
                <div className="bg-teal-100 w-12 h-12 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-6 border-l-4 border-blue-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 text-sm font-medium mb-1">This Month</p>
                  <p className="text-3xl font-bold text-gray-800">{thisMonth}</p>
                </div>
                <div className="bg-blue-100 w-12 h-12 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-6 border-l-4 border-purple-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 text-sm font-medium mb-1">This Week</p>
                  <p className="text-3xl font-bold text-gray-800">{thisWeek}</p>
                </div>
                <div className="bg-purple-100 w-12 h-12 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Completed Topics List */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden border-t-4 border-teal-500">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-2xl font-bold text-gray-800">Your Achievements</h2>
            <p className="text-gray-600 mt-1">All topics you've successfully completed</p>
          </div>

          {completedTopics.length === 0 ? (
            <div className="p-12 text-center">
              <div className="bg-gray-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
              <p className="text-gray-600 font-medium text-lg">No completed topics yet</p>
              <p className="text-gray-500 text-sm mt-2">Start learning and complete your first topic!</p>
              <button
                onClick={() => navigate('/home')}
                className="mt-6 px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-semibold"
              >
                Explore Topics
              </button>
            </div>
          ) : (
            <div>
              {/* Desktop: Cards View */}
              <div className="hidden md:block p-6 space-y-4">
                {completedTopics.map((topic, index) => (
                  <div
                    key={topic.id}
                    className="border-2 border-gray-100 rounded-xl p-6 hover:border-teal-200 hover:shadow-md transition-all"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4 flex-1">
                        <div className="bg-teal-100 w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0">
                          <svg className="w-6 h-6 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                        <div className="flex-1">
                          <h3 className="text-lg font-bold text-gray-800 mb-1">{topic.name}</h3>
                          <div className="flex items-center gap-4 text-sm">
                            {topic.category && (
                              <span className="px-3 py-1 bg-teal-100 text-teal-700 rounded-full text-xs font-semibold">
                                {topic.category}
                              </span>
                            )}
                            <span className="text-gray-500 flex items-center gap-1">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                              {new Date(topic.completion_date).toLocaleDateString("en-GB", {
                                day: 'numeric',
                                month: 'long',
                                year: 'numeric'
                              })}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="px-4 py-2 bg-teal-100 text-teal-700 rounded-lg font-bold text-sm">
                          ✓ Completed
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Mobile: Compact Cards */}
              <div className="md:hidden p-4 space-y-3">
                {completedTopics.map((topic) => (
                  <div
                    key={topic.id}
                    className="bg-gray-50 rounded-xl p-4 border-l-4 border-teal-500"
                  >
                    <div className="flex items-start gap-3">
                      <div className="bg-teal-100 w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0">
                        <svg className="w-5 h-5 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-bold text-gray-800 mb-2">{topic.name}</h3>
                        {topic.category && (
                          <span className="inline-block px-2 py-1 bg-teal-100 text-teal-700 rounded-full text-xs font-semibold mb-2">
                            {topic.category}
                          </span>
                        )}
                        <p className="text-xs text-gray-500">
                          {new Date(topic.completion_date).toLocaleDateString("en-GB")}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Motivational Card */}
        {totalCompleted > 0 && (
          <div className="mt-8 bg-gradient-to-br from-teal-500 to-teal-600 rounded-2xl shadow-xl p-8 text-white">
            <div className="flex items-center gap-4 mb-4">
              <div className="bg-white bg-opacity-20 w-14 h-14 rounded-full flex items-center justify-center flex-shrink-0">
                <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                </svg>
              </div>
              <div>
                <h3 className="text-2xl font-bold">Excellent Progress!</h3>
                <p className="text-teal-100">Keep up the fantastic work</p>
              </div>
            </div>
            <p className="text-teal-50 text-lg">
              You've completed <span className="font-bold">{totalCompleted}</span> topic{totalCompleted !== 1 ? 's' : ''}.
              {thisWeek > 0 && ` That's ${thisWeek} this week alone!`} Continue your learning journey to master GCSE Maths.
            </p>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default CompletedTopics;